import type { CSSProperties } from 'react';

type ThemeMessageKey =
	| 'theme_dark'
	| 'theme_light'
	| 'theme_tokyo_night'
	| 'theme_catppuccin_mocha'
	| 'theme_catppuccin_latte'
	| 'theme_gruvbox'
	| 'theme_rose_pine_moon'
	| 'theme_rose_pine_dawn'
	| 'theme_osaka_jade'
	| 'theme_shades_of_purple'
	| 'theme_supabase'
	| 'theme_github'
	| 'theme_one_dark_pro'
	| 'theme_system';

export type Layer = {
	image: string;
	size?: string;
	position?: string;
	blend?: CSSProperties['mixBlendMode'];
	opacity?: number;
};

export type Backdrop = {
	layers: Layer[];
};

type PhotoOptions = Omit<Layer, 'image'>;

function photo(path: string, opts: PhotoOptions = {}): Layer {
	const {
		blend = 'luminosity',
		size = 'min(42%, 30rem) auto',
		position = 'center',
		opacity,
	} = opts;
	return {
		image: `url("${path}")`,
		size,
		position,
		blend,
		opacity,
	};
}

function asset(name: string) {
	return new URL(`../assets/${name}`, import.meta.url).href;
}

type ThemeDefinition = {
	color: string | null;
	variant: 'light' | 'dark' | null;
	icon: string;
	messageKey: ThemeMessageKey;
	preview: {
		background: string;
		surface: string;
		muted: string;
		foreground: string;
		primary: string;
		accent: string;
	};
	backdrop?: Backdrop;
};

export const THEME_DEFINITIONS = {
	'moon-script': {
		color: '#071828',
		variant: 'dark',
		icon: 'lucide:moon',
		messageKey: 'theme_dark',
		preview: {
			background: '#071828',
			surface: '#0d2a40',
			muted: '#1a3852',
			foreground: '#e0e6ed',
			primary: '#6fcbff',
			accent: '#5f96ac',
		},
	},
	'sun-script': {
		color: '#edf4f7',
		variant: 'light',
		icon: 'lucide:sun',
		messageKey: 'theme_light',
		preview: {
			background: '#edf4f7',
			surface: '#f8fbfc',
			muted: '#d6e6ec',
			foreground: '#0b2234',
			primary: '#075b7a',
			accent: '#326b7e',
		},
	},
	'tokyo-night': {
		color: '#1a1b26',
		variant: 'dark',
		icon: 'fluent-emoji-high-contrast:tokyo-tower',
		messageKey: 'theme_tokyo_night',
		preview: {
			background: '#1a1b26',
			surface: '#24283b',
			muted: '#292e42',
			foreground: '#c0caf5',
			primary: '#7aa2f7',
			accent: '#bb9af7',
		},
		backdrop: {
			layers: [
				photo(asset('tokyo-tower.webp'), {
					position: 'right bottom',
					opacity: 0.52,
				}),
			],
		},
	},
	'catppuccin-mocha': {
		color: '#1e1e2e',
		variant: 'dark',
		icon: 'lucide:coffee',
		messageKey: 'theme_catppuccin_mocha',
		preview: {
			background: '#1e1e2e',
			surface: '#313244',
			muted: '#45475a',
			foreground: '#cdd6f4',
			primary: '#89b4fa',
			accent: '#cba6f7',
		},
		backdrop: {
			layers: [
				photo(asset('cafe.webp'), { position: 'left bottom', opacity: 0.3 }),
			],
		},
	},
	'catppuccin-latte': {
		color: '#eff1f5',
		variant: 'light',
		icon: 'lucide:coffee',
		messageKey: 'theme_catppuccin_latte',
		preview: {
			background: '#eff1f5',
			surface: '#ccd0da',
			muted: '#e6e9ef',
			foreground: '#4c4f69',
			primary: '#1e66f5',
			accent: '#8839ef',
		},
		backdrop: {
			layers: [
				photo(asset('desk-window.webp'), {
					blend: 'multiply',
					position: 'right bottom',
					opacity: 0.34,
				}),
			],
		},
	},
	gruvbox: {
		color: '#282828',
		variant: 'dark',
		icon: 'lucide:leaf',
		messageKey: 'theme_gruvbox',
		preview: {
			background: '#282828',
			surface: '#3c3836',
			muted: '#504945',
			foreground: '#ebdbb2',
			primary: '#d79921',
			accent: '#b16286',
		},
		backdrop: {
			layers: [
				photo(asset('forest-cabin.webp'), {
					position: 'left bottom',
					opacity: 0.3,
				}),
			],
		},
	},
	'rose-pine-moon': {
		color: '#191724',
		variant: 'dark',
		icon: 'lucide:flower-2',
		messageKey: 'theme_rose_pine_moon',
		preview: {
			background: '#191724',
			surface: '#26233a',
			muted: '#403d52',
			foreground: '#e0def4',
			primary: '#9ccfd8',
			accent: '#c4a7e7',
		},
		backdrop: {
			layers: [
				photo(asset('flower-stairs.webp'), {
					position: 'left bottom',
					opacity: 0.4,
				}),
			],
		},
	},
	'rose-pine-dawn': {
		color: '#faf4ed',
		variant: 'light',
		icon: 'lucide:flower-2',
		messageKey: 'theme_rose_pine_dawn',
		preview: {
			background: '#faf4ed',
			surface: '#f2e9e1',
			muted: '#e4d9cf',
			foreground: '#575279',
			primary: '#56949f',
			accent: '#907aa9',
		},
		backdrop: {
			layers: [
				photo(asset('garden-terrace.webp'), {
					blend: 'multiply',
					position: 'right bottom',
					opacity: 0.4,
				}),
			],
		},
	},
	'osaka-jade': {
		color: '#111c18',
		variant: 'dark',
		icon: 'maki:bank-jp',
		messageKey: 'theme_osaka_jade',
		preview: {
			background: '#111c18',
			surface: '#23372b',
			muted: '#32473b',
			foreground: '#f6f5dd',
			primary: '#509475',
			accent: '#d2689c',
		},
		backdrop: {
			layers: [
				photo(asset('japanese-street.webp'), {
					position: 'left bottom',
					size: 'min(42%, 28rem) auto',
					opacity: 0.6,
				}),
				photo(asset('coastal.webp'), {
					position: 'right bottom',
					size: 'min(42%, 32rem) auto',
					opacity: 0.6,
				}),
			],
		},
	},
	'shades-of-purple': {
		color: '#2d2b55',
		variant: 'dark',
		icon: 'fluent:circle-shadow-24-filled',
		messageKey: 'theme_shades_of_purple',
		preview: {
			background: '#2d2b55',
			surface: '#222244',
			muted: '#1e1e3f',
			foreground: '#ffffff',
			primary: '#fad000',
			accent: '#b362ff',
		},
		backdrop: {
			layers: [
				photo(asset('observatory.webp'), {
					position: 'right bottom',
					size: 'min(42%, 38rem) auto',
					opacity: 0.4,
				}),
			],
		},
	},
	supabase: {
		color: '#161616',
		variant: 'dark',
		icon: 'lineicons:supabase',
		messageKey: 'theme_supabase',
		preview: {
			background: '#161616',
			surface: '#232323',
			muted: '#303030',
			foreground: '#ededed',
			primary: '#3ecf8e',
			accent: '#a7f3d0',
		},
	},
	github: {
		color: '#0d1117',
		variant: 'dark',
		icon: 'lucide:github',
		messageKey: 'theme_github',
		preview: {
			background: '#0d1117',
			surface: '#161b22',
			muted: '#21262d',
			foreground: '#f0f6fc',
			primary: '#58a6ff',
			accent: '#bc8cff',
		},
	},
	'one-dark-pro': {
		color: '#282c34',
		variant: 'dark',
		icon: 'mdi:atom',
		messageKey: 'theme_one_dark_pro',
		preview: {
			background: '#282c34',
			surface: '#2c313c',
			muted: '#3e4451',
			foreground: '#e6e6e6',
			primary: '#61afef',
			accent: '#c678dd',
		},
		backdrop: {
			layers: [
				photo(asset('city.webp'), {
					position: 'right bottom',
					size: 'min(42%, 38rem) auto',
					opacity: 0.6,
				}),
			],
		},
	},
	auto: {
		color: null,
		variant: null,
		icon: 'lucide:monitor',
		messageKey: 'theme_system',
		preview: {
			background: '#071828',
			surface: '#0d2a40',
			muted: '#1a3852',
			foreground: '#e0e6ed',
			primary: '#6fcbff',
			accent: '#5f96ac',
		},
	},
} as const satisfies Record<string, ThemeDefinition>;

export type ThemeMode = keyof typeof THEME_DEFINITIONS;

export type ResolvedTheme = Exclude<ThemeMode, 'auto'>;

export function isLightTheme(theme: ResolvedTheme): boolean {
	return THEME_DEFINITIONS[theme].variant === 'light';
}

export const THEME_MODES = Object.keys(THEME_DEFINITIONS) as ThemeMode[];
export const THEME_CLASSES = THEME_MODES.filter(
	(mode): mode is ResolvedTheme => mode !== 'auto',
);
export const THEME_LIGHT_CLASSES = THEME_CLASSES.filter(isLightTheme);
export const THEME_LIGHT_SELECTORS = [
	'.light',
	...THEME_LIGHT_CLASSES.map((theme) => `.${theme}`),
].join(', ');

export const DEFAULT_THEME_BY_SCHEME = {
	dark: 'moon-script',
	light: 'sun-script',
} as const satisfies Record<'dark' | 'light', ResolvedTheme>;

export const THEME_COLORS = Object.fromEntries(
	THEME_CLASSES.map((mode) => [mode, THEME_DEFINITIONS[mode].color]),
) as Record<ResolvedTheme, string>;

const LEGACY_THEME_MIGRATIONS: Record<string, ThemeMode> = {
	dark: 'moon-script',
	light: 'sun-script',
	'rose-pine': 'rose-pine-moon',
};

export function normalizeThemeMode(value: string | null): ThemeMode | null {
	if (!value) return null;
	const migrated = LEGACY_THEME_MIGRATIONS[value] ?? value;
	return Object.hasOwn(THEME_DEFINITIONS, migrated)
		? (migrated as ThemeMode)
		: null;
}

const THEME_BOOT_CONFIG = JSON.stringify({
	classes: THEME_CLASSES,
	colors: THEME_COLORS,
	lightThemes: THEME_LIGHT_CLASSES,
	defaults: DEFAULT_THEME_BY_SCHEME,
	migrations: LEGACY_THEME_MIGRATIONS,
	modes: THEME_MODES,
});

export const THEME_INIT_SCRIPT = `(function(){try{var config=${THEME_BOOT_CONFIG};var stored=window.localStorage.getItem('theme');stored=config.migrations[stored]||stored;var mode=config.modes.indexOf(stored)>-1?stored:'auto';var prefersDark=window.matchMedia('(prefers-color-scheme: dark)').matches;var resolved=mode==='auto'?(prefersDark?config.defaults.dark:config.defaults.light):mode;var root=document.documentElement;root.classList.remove('light','dark');config.classes.forEach(function(theme){root.classList.remove(theme)});var isLight=config.lightThemes.indexOf(resolved)>-1;root.classList.add(isLight?'light':'dark',resolved);if(mode==='auto'){root.removeAttribute('data-theme')}else{root.setAttribute('data-theme',mode)}root.style.colorScheme=isLight?'light':'dark';var meta=document.querySelector('meta[name="theme-color"]');if(meta)meta.content=config.colors[resolved]||'#071828'}catch(e){}})();`;
